import { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { FillBlankItem } from '../../services/mockQuizService';
import type { QuizProgress } from '../QuizBlock';

interface FillBlankPreviewProps {
  items: FillBlankItem[];
  initialAnswers?: Record<number, { input: string; correct: boolean }>;
  onAnswersChange?: (answers: Record<number, { input: string; correct: boolean }>) => void;
  onProgressChange?: (progress: QuizProgress) => void;
}

export const FillBlankPreview = ({ items, initialAnswers, onAnswersChange, onProgressChange }: FillBlankPreviewProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [userInput, setUserInput] = useState('');
  const [answers, setAnswers] = useState<Record<number, { input: string; correct: boolean }>>(initialAnswers || {});

  const currentItem = items[currentIndex];
  const currentAnswer = answers[currentIndex];
  const showResult = currentAnswer !== undefined;
  const isCorrect = currentAnswer?.correct;

  // Calculate and report progress
  const updateProgress = useCallback(() => {
    const completed = Object.keys(answers).length;
    const correct = Object.values(answers).filter(a => a.correct).length;
    onProgressChange?.({ total: items.length, completed, correct });
  }, [answers, items.length, onProgressChange]);

  useEffect(() => {
    updateProgress();
  }, [updateProgress]);

  // Reset input when navigating to unanswered question
  useEffect(() => {
    if (!answers[currentIndex]) {
      setUserInput('');
    }
  }, [currentIndex, answers]);

  const checkAnswer = (answer: string): boolean => {
    if (!currentItem) return false;
    const normalized = answer.trim().toLowerCase();
    const correct = currentItem.answer.toLowerCase();
    if (normalized === correct) return true;
    return currentItem.accepted_variants?.some(v => v.toLowerCase() === normalized) || false;
  };

  const handleSubmit = () => {
    if (userInput.trim()) {
      const correct = checkAnswer(userInput);
      const newAnswers = {
        ...answers,
        [currentIndex]: { input: userInput, correct }
      };
      setAnswers(newAnswers);
      onAnswersChange?.(newAnswers);
    }
  };

  const handleNext = () => {
    if (currentIndex < items.length - 1) {
      setCurrentIndex(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
  };

  if (!currentItem) return null;

  // Split prompt at blank
  const parts = currentItem.prompt.split('__________');
  const displayAnswer = showResult ? currentAnswer.input : userInput;

  return (
    <div className="py-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <span className="text-xs text-slate-400">
          Question {currentIndex + 1} of {items.length}
        </span>
        <div className="flex items-center gap-1">
          <button
            onClick={handlePrev}
            disabled={currentIndex === 0}
            className="p-1.5 rounded-md hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed text-slate-400 hover:text-slate-600 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            onClick={handleNext}
            disabled={currentIndex === items.length - 1}
            className="p-1.5 rounded-md hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed text-slate-400 hover:text-slate-600 transition-colors"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Prompt with blank */}
      <div className="bg-slate-50 rounded-lg p-4 mb-4">
        <p className="text-sm text-slate-700 leading-relaxed">
          {parts[0]}
          <span className="inline-block mx-1">
            {showResult ? (
              <span className={`px-2 py-0.5 rounded ${isCorrect ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                {displayAnswer}
              </span>
            ) : (
              <input
                type="text"
                value={userInput}
                onChange={(e) => setUserInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
                placeholder="type answer..."
                className="inline-block w-32 px-2 py-0.5 bg-white border border-slate-300 rounded text-center text-sm focus:outline-none focus:border-slate-400"
              />
            )}
          </span>
          {parts[1]}
        </p>
      </div>

      {/* Submit Button */}
      {!showResult && userInput.trim() && (
        <button
          onClick={handleSubmit}
          className="px-4 py-2 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800 transition-colors"
        >
          Check Answer
        </button>
      )}

      {/* Result */}
      {showResult && (
        <div className={`p-3 rounded-lg text-sm ${isCorrect ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'}`}>
          <p className="font-medium mb-1">
            {isCorrect ? '✓ Correct!' : '✗ Incorrect'}
          </p>
          {!isCorrect && (
            <p className="text-slate-600 mb-2">
              Correct answer: <span className="font-medium">{currentItem.answer}</span>
            </p>
          )}
          {currentItem.explanation && (
            <p className="text-slate-500 text-xs">{currentItem.explanation}</p>
          )}
        </div>
      )}

      {/* Progress dots */}
      <div className="flex justify-center gap-1 mt-4">
        {items.map((_, idx) => {
          const answered = answers[idx] !== undefined;
          const wasCorrect = answers[idx]?.correct;
          return (
            <button
              key={idx}
              onClick={() => setCurrentIndex(idx)}
              className={`w-2 h-2 rounded-full transition-colors ${
                idx === currentIndex
                  ? 'bg-slate-600 ring-2 ring-slate-300'
                  : answered
                  ? wasCorrect ? 'bg-emerald-400' : 'bg-red-400'
                  : 'bg-slate-200 hover:bg-slate-300'
              }`}
            />
          );
        })}
      </div>
    </div>
  );
};
